/**
 * The video editor — ours, because the platform's does not work in an embed.
 *
 * `video`'s `studio_view` renders 7.7 KB containing no form controls at all: its fields are
 * built at runtime by JavaScript that Studio's own page bundles and an embedded fragment
 * never loads. Authors got a modal with a heading and a Close button and nowhere to put a
 * link. A source editor is no help either — a video block's `data` is present but always
 * empty, so it would be an equally blank box. Everything lives in settings fields, so this
 * edits those.
 *
 * Deliberately the fields an author actually reaches for, not all thirty a VideoBlock has.
 * The rest keep their values untouched because only what is sent is written.
 */

import { useState } from 'react';
import { Button } from '../../shared/ui';
import { saveVideoFields, type VideoFields } from '../../shared/lib/editor';
import './component-editor.css';

type Props = {
  blockKey: string;
  initialFields: VideoFields;
  initialName?: string;
  readOnly?: boolean;
  onSaved?: () => void;
};

/** A video may have several sources; the form edits them as one per line. */
const toLines = (v?: string[]) => (v ?? []).join('\n');
const fromLines = (text: string) =>
  text.split('\n').map((s) => s.trim()).filter(Boolean);

export default function VideoEditor({
  blockKey,
  initialFields,
  initialName,
  readOnly = false,
  onSaved,
}: Props) {
  const [name, setName] = useState(initialName ?? '');
  const [videoId, setVideoId] = useState(initialFields.edx_video_id ?? '');
  const [sources, setSources] = useState(toLines(initialFields.html5_sources));
  const [youtube, setYoutube] = useState(initialFields.youtube_id_1_0 ?? '');
  const [transcript, setTranscript] = useState(initialFields.sub ?? '');
  const [allowDownload, setAllowDownload] = useState(Boolean(initialFields.download_video));
  const [showCaptions, setShowCaptions] = useState(initialFields.show_captions !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function onSave() {
    setSaving(true);
    setError(null);
    try {
      await saveVideoFields(
        blockKey,
        {
          edx_video_id: videoId.trim(),
          html5_sources: fromLines(sources),
          youtube_id_1_0: youtube.trim(),
          sub: transcript.trim(),
          download_video: allowDownload,
          show_captions: showCaptions,
        },
        name.trim() || undefined,
      );
      setSaved(true);
      onSaved?.();
    } catch {
      // Deliberately not "try again": a refused save usually means the draft is stale or
      // the session expired, and re-clicking repeats it. Say what is true and stop.
      setError('That did not save. Reopen the component to get the current version.');
    } finally {
      setSaving(false);
    }
  }

  const dirty = () => setSaved(false);

  return (
    <div className="ce-body">
      <p className="ce-note">
        This platform builds the standard video editor in the browser, so it cannot be
        embedded here. These are the same fields.
      </p>

      <label className="ce-field">
        <span className="ce-label">Name</span>
        <input
          className="ce-input"
          value={name}
          disabled={readOnly}
          onChange={(e) => { setName(e.target.value); dirty(); }}
        />
      </label>

      <label className="ce-field">
        <span className="ce-label">Video URL</span>
        <textarea
          className="ce-input ce-input--multiline"
          rows={2}
          value={sources}
          disabled={readOnly}
          placeholder="https://…/video.mp4  or  https://stream.mux.com/….m3u8"
          onChange={(e) => { setSources(e.target.value); dirty(); }}
        />
        <span className="ce-hint">One per line if the video has more than one source.</span>
      </label>

      <label className="ce-field">
        <span className="ce-label">YouTube ID</span>
        <input
          className="ce-input"
          value={youtube}
          disabled={readOnly}
          placeholder="dQw4w9WgXcQ"
          onChange={(e) => { setYoutube(e.target.value); dirty(); }}
        />
        <span className="ce-hint">The id only, not the whole watch URL.</span>
      </label>

      <label className="ce-field">
        <span className="ce-label">Video ID</span>
        <input
          className="ce-input"
          value={videoId}
          disabled={readOnly}
          onChange={(e) => { setVideoId(e.target.value); dirty(); }}
        />
        <span className="ce-hint">
          Set when the video was uploaded through the platform. Leave it alone unless you
          know it changed.
        </span>
      </label>

      <label className="ce-field">
        <span className="ce-label">Transcript file</span>
        <input
          className="ce-input"
          value={transcript}
          disabled={readOnly}
          onChange={(e) => { setTranscript(e.target.value); dirty(); }}
        />
      </label>

      <div className="ce-checks">
        <label className="ce-check">
          <input
            type="checkbox"
            checked={showCaptions}
            disabled={readOnly}
            onChange={(e) => { setShowCaptions(e.target.checked); dirty(); }}
          />
          <span>Show captions</span>
        </label>
        <label className="ce-check">
          <input
            type="checkbox"
            checked={allowDownload}
            disabled={readOnly}
            onChange={(e) => { setAllowDownload(e.target.checked); dirty(); }}
          />
          <span>Allow download</span>
        </label>
      </div>

      {error && <p className="ce-error">{error}</p>}

      {!readOnly && (
        <div className="ce-actions">
          <Button variant="primary" busy={saving} onClick={onSave}>
            {saved ? 'Saved' : 'Save'}
          </Button>
          <span className="ce-hint">
            Saves a draft. Publish the unit before learners see it.
          </span>
        </div>
      )}
    </div>
  );
}
